// « Mains vives » (D-163, D-164) : la vidéo, enregistrée SUR LE TÉLÉPHONE à partir du canevas du jeu (captureStream +
// MediaRecorder), en MP4 quand le navigateur sait le faire (iPhone, Chrome récent), sinon en WebM. Jamais envoyée : elle vit
// dans la mémoire de la page jusqu'à « Garder la vidéo » (la feuille de partage, puis Photos) ou « Effacer ». 90 s au plus.
import { VIDEO_MAX_S } from '../domain/mains-vives';

const TYPES = ['video/mp4;codecs=avc1.42E01E', 'video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];

export interface Clip { blob: Blob; url: string; type: string; ext: 'mp4' | 'webm'; seconds: number; partial: boolean }

/** Le format que ce navigateur sait enregistrer (null : pas de vidéo, le jeu marche quand même). */
export function recordType(): string | null {
  const R = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (!R || typeof R.isTypeSupported !== 'function') return null;
  if (typeof HTMLCanvasElement === 'undefined' || !('captureStream' in HTMLCanvasElement.prototype)) return null;
  return TYPES.find(t => { try { return R.isTypeSupported(t); } catch { return false; } }) ?? null;
}

export class LocalRecorder {
  private rec: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private bytes = 0;
  private limit: ReturnType<typeof setTimeout> | null = null;
  private started = 0;
  private pausedAt = 0;
  private pausedMs = 0;
  private cut = false;
  private done: Promise<Clip | null> | null = null;

  constructor(private readonly maxSeconds = VIDEO_MAX_S, private readonly maxBytes = 80 * 1024 * 1024, private readonly bitrate = 2_500_000) {}

  get active(): boolean { return !!this.rec && this.rec.state !== 'inactive'; }

  /** Commence à enregistrer le canevas ; faux si ce navigateur ne sait pas. */
  start(canvas: HTMLCanvasElement): boolean {
    const type = recordType();
    if (!type || this.rec) return false;
    try {
      this.stream = canvas.captureStream(30);
      const rec = new MediaRecorder(this.stream, { mimeType: type, videoBitsPerSecond: this.bitrate });
      this.rec = rec;
      this.chunks = []; this.bytes = 0; this.cut = false; this.pausedMs = 0;
      rec.ondataavailable = e => { if (e.data?.size) { this.chunks.push(e.data); this.bytes += e.data.size; if (this.bytes >= this.maxBytes) { this.cut = true; void this.stop(); } } };
      this.done = new Promise(resolve => {
        rec.onstop = () => {
          if (this.limit) clearTimeout(this.limit);
          this.stream?.getTracks().forEach(t => t.stop());
          const mime = rec.mimeType || type;
          const blob = new Blob(this.chunks, { type: mime.split(';')[0] });
          this.chunks = [];
          const seconds = Math.min(this.maxSeconds, Math.round((performance.now() - this.started - this.pausedMs) / 1000));
          resolve(blob.size ? { blob, url: URL.createObjectURL(blob), type: blob.type, ext: blob.type.includes('mp4') ? 'mp4' : 'webm', seconds, partial: this.cut } : null);
        };
        rec.onerror = () => { this.cut = true; try { if (rec.state !== 'inactive') rec.stop(); } catch { resolve(null); } };
      });
      rec.start(1000);
      this.started = performance.now();
      // Le plafond : 90 s de vidéo, pauses non comprises.
      const arm = (ms: number) => { this.limit = setTimeout(() => { this.cut = true; void this.stop(); }, ms); };
      arm(this.maxSeconds * 1000);
      this.rearm = arm;
      return true;
    } catch {
      this.stream?.getTracks().forEach(t => t.stop());
      this.rec = null; this.stream = null;
      return false;
    }
  }
  private rearm: ((ms: number) => void) | null = null;

  pause(): void {
    if (this.rec?.state !== 'recording') return;
    try { this.rec.pause(); } catch { return; }
    this.pausedAt = performance.now();
    if (this.limit) clearTimeout(this.limit);
  }
  resume(): void {
    if (this.rec?.state !== 'paused') return;
    try { this.rec.resume(); } catch { return; }
    this.pausedMs += performance.now() - this.pausedAt;
    const left = this.maxSeconds * 1000 - (performance.now() - this.started - this.pausedMs);
    this.rearm?.(Math.max(0, left));
  }
  /** Arrête et rend la vidéo (null : rien d'enregistré). */
  async stop(): Promise<Clip | null> {
    const r = this.rec;
    if (!r) return null;
    if (r.state !== 'inactive') { try { r.stop(); } catch { /* déjà arrêté */ } }
    const clip = await (this.done ?? Promise.resolve(null));
    this.rec = null;
    return clip;
  }
  /** Tout jeter (la page se ferme pendant l'enregistrement). */
  abort(): void {
    if (this.limit) clearTimeout(this.limit);
    try { if (this.rec && this.rec.state !== 'inactive') this.rec.stop(); } catch { /* déjà arrêté */ }
    this.stream?.getTracks().forEach(t => t.stop());
    this.rec = null; this.chunks = [];
  }
}
